import { Badge, Icon } from "@elaniintc/goes-design-system";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import dayjs from "dayjs";

interface AssociationDetailDocument {
  id: string;
  name: string;
  url: string;
}

interface AssociationDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  variant: "representative" | "representation";
  data: {
    name: string;
    email?: string;
    active: boolean;
    createdAt?: string;
    documents?: AssociationDetailDocument[];
  } | null;
}

export const AssociationDetailModal = ({
  isOpen,
  onClose,
  variant,
  data,
}: AssociationDetailModalProps) => {
  if (!data) return null;

  const title =
    variant === "representative"
      ? "Detalle de representante"
      : "Detalle de representación";

  const formattedDate = data.createdAt
    ? dayjs(data.createdAt).format("DD/MM/YYYY")
    : "";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        showCloseButton={false}
        className="max-w-191 p-0 overflow-hidden"
      >
        {/* Header */}
        <DialogHeader className="flex flex-row items-center justify-between px-6 pt-6 pb-0">
          <DialogTitle className="text-h6 font-semibold text-heading text-left">
            {title}
          </DialogTitle>
          <button
            type="button"
            onClick={onClose}
            className="size-8 flex items-center justify-center rounded-md border border-neutral-quiet hover:bg-neutral-background transition-colors"
          >
            <Icon type="far" name="xmark" className="text-sm text-body" />
          </button>
        </DialogHeader>

        <div className="px-0">
          <hr className="border-neutral-quiet" />
        </div>

        <div className="flex flex-col gap-4 px-6 pb-6 pt-4">
          {/* Card de persona */}
          <div className="border border-neutral-quiet rounded-lg overflow-hidden">
            {/* Nombre + badge */}
            <div className="flex items-center gap-3 px-4 py-3 bg-neutral-object-accent-silent">
              <div className="size-10 rounded-full bg-surface-action-accent-quiet flex items-center justify-center shrink-0 border-[0.5px] border-action-quiet">
                <Icon
                  type="fas"
                  name="user"
                  className="text-sm text-action-quiet"
                />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-body-loud truncate">
                  {data.name}
                </p>
                <Badge size="xs" variant={data.active ? "success" : "neutral"}>
                  {data.active ? "Activo" : "Inactivo"}
                </Badge>
              </div>
            </div>

            {/* Email */}
            {data.email && (
              <div className="flex items-center gap-3 px-4 py-2.5 border-t border-neutral-quiet bg-neutral-object-accent-silent">
                <div className="size-8 rounded-lg bg-neutral-background border border-neutral-quiet flex items-center justify-center shrink-0">
                  <Icon
                    type="far"
                    name="envelope"
                    className="text-sm text-body"
                  />
                </div>
                <p className="text-sm text-body">{data.email}</p>
              </div>
            )}

            {/* Fecha */}
            {formattedDate && (
              <div className="flex items-center gap-3 px-4 py-2.5 border-t border-neutral-quiet bg-neutral-object-accent-silent">
                <div className="size-8 rounded-lg bg-neutral-background border border-neutral-quiet flex items-center justify-center shrink-0">
                  <Icon
                    type="far"
                    name="calendar-star"
                    className="text-sm text-body"
                  />
                </div>
                <p className="text-sm text-body">Rep. desde {formattedDate}</p>
              </div>
            )}

            {/* Documentación adjunta */}
            <div className="flex items-start gap-3 px-4 py-2.5 border-t border-neutral-quiet bg-neutral-object-accent-silent">
              <div className="size-8 rounded-lg bg-neutral-background border border-neutral-quiet flex items-center justify-center shrink-0">
                <Icon
                  type="far"
                  name="files"
                  className="text-sm text-body mt-0.5"
                />
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-sm text-body">Documentación adjunta</p>
                {data.documents && data.documents.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {data.documents.map((doc) => (
                      <a
                        key={doc.id}
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Badge size="xs" variant="neutral" shape="square">
                          <Icon
                            type="fas"
                            name="file"
                            className="text-body-quiet"
                          />
                          {doc.name}
                        </Badge>
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-body-quiet">Sin documentos</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
